import { motion } from "framer-motion";
import { ShieldCheck, Beaker, FileText, Wrench, Award, Eye } from "lucide-react";

const PILLARS = [
  {
    icon: Beaker,
    title: "Recognised methodologies",
    desc: "Testing conducted using laboratory methods based on recognised Australian and international standards where applicable.",
  },
  {
    icon: FileText,
    title: "Documented procedures",
    desc: "Every test follows documented quality-assurance procedures from sample receipt through to results.",
  },
  {
    icon: Wrench,
    title: "Calibrated equipment",
    desc: "Laboratory equipment is maintained, calibrated and operated according to documented procedures.",
  },
  {
    icon: ShieldCheck,
    title: "Quality control",
    desc: "Quality control checks are incorporated into our laboratory workflows on every batch.",
  },
  {
    icon: Eye,
    title: "Reviewed before release",
    desc: "Results are reviewed and presented in an easy-to-understand format before they reach you.",
  },
  {
    icon: Award,
    title: "Practical & focused",
    desc: "Designed specifically for rainwater, bore water and rural property owners — with direct access to water-treatment specialists when needed.",
  },
];

export const QualityAssurance = () => {
  return (
    <section id="quality" className="relative py-12 lg:py-16 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-600">Trust & Transparency</span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.05]">
              Reliable, consistent <span className="water-text">water quality insights.</span>
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Aware Water Testing is a local water testing laboratory focused on fast turnaround, professional reporting and practical recommendations for homeowners, property managers, plumbers and rural property owners.
            </p>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              Testing is performed using documented procedures and industry-recognised laboratory methods — designed to deliver reliable, consistent and practical results you can actually use.
            </p>

            <div className="mt-7 inline-flex items-start gap-3 rounded-2xl bg-slate-50 p-5 border border-slate-100 max-w-md">
              <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-cyan-600">Practical Lab Analysis</div>
                <div className="text-sm text-slate-700 mt-1 leading-relaxed">
                  Designed specifically for rainwater, bore water and rural properties — with plain-English answers and expert recommendations.
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-4">
              {PILLARS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <motion.div
                    key={p.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.45, delay: i * 0.05 }}
                    className="group p-5 rounded-2xl bg-slate-50 border border-slate-100 hover:border-cyan-200 hover:bg-white hover:shadow-md transition-all"
                  >
                    <div className="w-10 h-10 rounded-xl bg-white border border-slate-100 group-hover:bg-cyan-500 group-hover:border-cyan-500 flex items-center justify-center transition-colors">
                      <Icon className="w-5 h-5 text-slate-700 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="mt-4 font-display font-semibold text-slate-900 text-base leading-tight">{p.title}</h3>
                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
